
'use client';

import { useState, useEffect } from 'react';
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { AlertCircle, ArrowLeft, Check, CheckCircle, Clock, FileCheck, Fingerprint, Map, MapPin, Printer, Star, User, X, FileText, ChevronDown, ShieldCheck, Tag, Users, Car, CameraOff, AlertTriangle, XCircle, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import type { LatLngExpression } from 'leaflet';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Link from 'next/link';


const MapDisplay = dynamic(() => import('@/components/ui/map-display'), {
  ssr: false,
});
const MiniMapDisplay = dynamic(() => import('@/components/ui/mini-map-display'), {
  ssr: false,
});


// --- Types ---
type BiometricVerificationData = {
  title: string;
  dbPhoto: string;
  dbPhotoHint: string;
  appPhoto: string;
  appPhotoHint: string;
}

type Fault = {
  code: string;
  description: string;
  count: number;
};

type FaultCategory = {
  title: string;
  points: number;
  faults: Fault[];
  type: 'eliminatory' | 'severe' | 'medium' | 'light';
};

export type Aviso = {
  time: string;
  lat: number;
  lng: number;
  description: string;
  alert: {
    type: 'success' | 'warning' | 'destructive';
    text: string;
    icon: React.ElementType;
  };
  eventImage: string | null;
  eventHint: string | null;
  validated?: boolean;
  justification?: string;
};

// --- Helper Components ---

const InfoCard = ({ title, icon, children, className, ...props }: { title: string, icon: React.ReactNode, children: React.ReactNode, className?: string, id?: string }) => (
  <Card className={cn("shadow-md overflow-hidden", className)} {...props}>
    <CardHeader className="flex flex-row items-center gap-3 bg-gray-50 p-3 border-b">
      {icon}
      <CardTitle className="text-base font-semibold text-gray-700">{title}</CardTitle>
    </CardHeader>
    <CardContent className="p-6 text-sm">
      {children}
    </CardContent>
  </Card>
);

const InfoGrid = ({ children }: { children: React.ReactNode }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4">
    {children}
  </div>
);

const InfoItem = ({ label, value }: { label: string, value: string | React.ReactNode }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="font-medium text-gray-800">{value}</p>
  </div>
);

const StarRating = ({ rating, label }: { rating: number, label: string }) => (
    <div className="flex items-center justify-between">
        <span>{label}</span>
        <div className="flex items-center gap-2">
            <div className="flex">
                {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`h-4 w-4 ${i < Math.round(rating / 20) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                ))}
            </div>
            <span className="font-bold text-sm text-gray-700 w-6 text-right">{rating.toFixed(1)}</span>
        </div>
    </div>
);

const SyllabusItem = ({ text, checked }: { text: string, checked: boolean }) => (
  <div className="flex items-center gap-3">
    <Checkbox id={text} checked={checked} disabled className="border-gray-400 data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
    <label htmlFor={text} className="text-sm text-gray-700">{text}</label>
  </div>
);

const SyllabusSection = ({ title, children }: { title: string, children: React.ReactNode }) => (
  <div>
    <h4 className="font-semibold text-gray-800 mb-3">{title}</h4>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6">
      {children}
    </div>
  </div>
);


// --- Page Component ---

export default function AulaReportPage() {
  const [generatedDate, setGeneratedDate] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<LatLngExpression | null>(null);
  const [selectedBiometric, setSelectedBiometric] = useState<BiometricVerificationData | null>(null);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [isConfirmAuditModalOpen, setIsConfirmAuditModalOpen] = useState(false);
  const [auditorId, setAuditorId] = useState('');
  const [auditStatus, setAuditStatus] = useState<'pending-audit' | 'validated' | 'invalidated'>('pending-audit');


  const initialAvisos: Aviso[] = [
    {
      time: '15:47:28',
      lat: -17.7615,
      lng: -48.6064,
      description: 'Início do percurso',
      alert: { type: 'success', text: 'Sem alerta', icon: Flag },
      eventImage: 'https://placehold.co/120x80.png',
      eventHint: 'car dashboard',
      validated: true,
      justification: '',
    },
    {
        time: '15:52:10',
        lat: -17.7645,
        lng: -48.6089,
        description: 'Verificado informações do Veiculo',
        alert: { type: 'success', text: 'Chassi: 9BWHE21B24E89243, Placa: JIK-1234', icon: FileCheck },
        eventImage: null,
        eventHint: null,
        validated: true,
        justification: '',
      },
    {
      time: '15:55:15',
      lat: -17.7620,
      lng: -48.6124,
      description: 'Mais de duas pessoas no veículo',
      alert: { type: 'destructive', text: 'Alerta: Presença não autorizada', icon: Users },
      eventImage: 'https://placehold.co/120x80.png',
      eventHint: 'three people car',
      validated: false,
      justification: '',
    },
    {
      time: '16:05:40',
      lat: -17.7665,
      lng: -48.6094,
      description: 'Veículo parado por mais de 10 minutos',
      alert: { type: 'warning', text: 'Alerta: Parada não programada', icon: Car },
      eventImage: 'https://placehold.co/120x80.png',
      eventHint: 'stopped car road',
      validated: true,
      justification: '',
    },
    {
      time: '16:15:02',
      lat: -17.7695,
      lng: -48.6069,
      description: 'Foto aleatória capturada',
      alert: { type: 'destructive', text: 'Alerta: Verificação de segurança', icon: CameraOff },
      eventImage: 'https://placehold.co/120x80.png',
      eventHint: 'blurry street',
      validated: false,
      justification: '',
    },
    {
      time: '16:33:40',
      lat: -17.7615,
      lng: -48.6024,
      description: 'Fim do percurso',
      alert: { type: 'success', text: 'Sem alerta', icon: Flag },
      eventImage: null,
      eventHint: null,
      validated: true,
      justification: '',
    },
  ];

  const [auditedAlerts, setAuditedAlerts] = useState<Aviso[]>(initialAvisos);

  useEffect(() => {
    const date = format(new Date(), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });
    setGeneratedDate(date);
  }, []);
  
  const handlePrint = () => {
    document.body.classList.add('print-mode');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('print-mode');
    }, 500);
  };

  const handleExportTXT = () => {
    const reportElement = document.querySelector('.printable-area');
    if (reportElement) {
      const textContent = (reportElement as HTMLElement).innerText;
      const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'relatorio_aula.txt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleExportXLS = () => {
    const data = [
      ["RELATÓRIO DE AULA PRÁTICA"],
      [],
      ["DADOS DA AULA"],
      ["Número da Aula", "257"],
      ["Data", "24/07/2025"],
      ["Horário Inicial", "15:43:40"],
      ["Horário Final", "16:33:40"],
      ["Duração da Aula", "50 minutos"],
      ["KM Inicial", "197.000 km"],
      ["KM Final", "197.008 km"],
      ["Distância Percorrida", "8,00 km"],
      [],
      ["INFORMAÇÕES DA AUTOESCOLA"],
      ["Nome", "Autoescola Segura no Trânsito"],
      ["CNPJ", "12.345.678/0001-90"],
      ["Diretor Geral", "Carlos Eduardo"],
      ["Credenciamento", "DETRAN-GO-12345"],
      ["Cidade/Estado", "Goiânia - GO"],
      [],
      ["INFORMAÇÕES DO ALUNO"],
      ["Nome Completo", "Pedro Wilson Arantes Moura"],
      ["CPF", "021.305.055"],
      ["Categoria", "B"],
      ["Processo", "Primeira Habilitação"],
      ["Renach", "GO123456789"],
      [],
      ["INFORMAÇÕES DO INSTRUTOR"],
      ["Nome Completo", "João Silva"],
      ["CPF", "123.456.789-00"],
      ["Credencial", "DETRAN-GO 54321"],
      ["Validade da Credencial", "31/12/2025"],
      [],
      ["INFORMAÇÕES DO VEÍCULO"],
      ["Modelo", "Volkswagen Gol"],
      ["Placa", "JIK-1234"],
      ["Ano", "2022"],
      ["Nº do Chassi", "9BWHE21B24E89243"],
      ["Credenciamento", "DETRAN-GO 98765"],
      [],
      ["FALTAS COMETIDAS"],
      ["Tipo de Falta", "Descrição", "Quantidade"],
      ...faultsData.flatMap(cat => cat.faults.filter(f => f.count > 0).map(f => [cat.title.split(' (')[0], f.description, f.count])),
      [],
      ["Total de Pontos", totalPoints],
      [],
      ["AVISOS DA AULA"],
      ["Horário", "Descrição", "Alerta", "Status"],
      ...auditedAlerts.map(a => [a.time, a.description, a.alert.text, a.validated ? 'Validado' : 'Invalidado']),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Relatório de Aula");

    // Adjust column widths
    const colWidths = [
        { wch: 30 }, { wch: 60 }, { wch: 15 }, { wch: 15 }
    ];
    worksheet['!cols'] = colWidths;
    
    XLSX.writeFile(workbook, "relatorio_aula.xlsx");
};


  const handleAuditCheck = (index: number, checked: boolean) => {
    const newAlerts = [...auditedAlerts];
    newAlerts[index].validated = checked;
    if (checked) {
        newAlerts[index].justification = ''; // Clear justification if validated
    }
    setAuditedAlerts(newAlerts);
  };
  
  const handleJustificationChange = (index: number, text: string) => {
    const newAlerts = [...auditedAlerts];
    newAlerts[index].justification = text;
    setAuditedAlerts(newAlerts);
  };

  const handleOpenConfirmModal = () => {
    setIsAuditModalOpen(false);
    setIsConfirmAuditModalOpen(true);
  };

  const handleConfirmAudit = (action: 'validate' | 'invalidate') => {
    // Here you would typically handle the final audit logic,
    // like sending the data (auditedAlerts and auditorId) to a server.
    console.log(`Aula ${action} pelo auditor ${auditorId}.`, auditedAlerts);
    alert(`Aula ${action === 'validate' ? 'Validada' : 'Invalidada'} com Sucesso!`);
    
    setAuditStatus(action === 'validate' ? 'validated' : 'invalidated');

    // Close modals and reset state
    setIsConfirmAuditModalOpen(false);
    setAuditorId('');
  };

  const handleExportPDF = async (mode: 'color' | 'bw') => {
    const reportContainer = document.querySelector('.printable-area') as HTMLElement;
    if (!reportContainer) return;

    alert(`Iniciando a geração do PDF ${mode === 'color' ? 'colorido' : 'preto e branco'}. Isso pode levar alguns segundos...`);

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'a4',
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const margin = 20;
    let y = margin;

    const addImageToPdf = (imgData: string, imgWidth: number, imgHeight: number) => {
      if (y + imgHeight > pdf.internal.pageSize.getHeight() - margin) {
        pdf.addPage();
        y = margin;
      }
      pdf.addImage(imgData, 'JPEG', margin, y, imgWidth, imgHeight);
      y += imgHeight + 10;
    };

    if (mode === 'bw') {
      document.body.classList.add('print-mode');
    }

    try {
      const headerElement = document.querySelector('#report-header') as HTMLElement;
      if (headerElement) {
        const canvas = await html2canvas(headerElement, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
        if (canvas.width > 0 && canvas.height > 0) {
            const imgData = canvas.toDataURL('image/jpeg', 1.0);
            const imgWidth = pdfWidth - margin * 2;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            addImageToPdf(imgData, imgWidth, imgHeight);
        }
      }

      const reportElements = Array.from(reportContainer.querySelectorAll<HTMLElement>('.report-card'));

      for (const element of reportElements) {
        const canvas = await html2canvas(element, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
         if (canvas.width > 0 && canvas.height > 0) {
            const imgData = canvas.toDataURL('image/jpeg', 1.0);
            const imgWidth = pdfWidth - margin * 2;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            addImageToPdf(imgData, imgWidth, imgHeight);
        }
      }

      pdf.save(`relatorio_aula_${mode}.pdf`);

    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Ocorreu um erro ao gerar o PDF. Por favor, tente novamente.");
    } finally {
      if (mode === 'bw') {
        document.body.classList.remove('print-mode');
      }
    }
  };

  const routeCoordinates: LatLngExpression[] = [
    [-17.7615, -48.6064], [-17.7620, -48.6064], [-17.7625, -48.6064],
    [-17.7630, -48.6064], [-17.7635, -48.6064], [-17.7640, -48.6064],
    [-17.7645, -48.6064], [-17.7645, -48.6069], [-17.7645, -48.6074],
    [-17.7645, -48.6079], [-17.7645, -48.6089], [-17.7645, -48.6094],
    [-17.7645, -48.6099], [-17.7640, -48.6094], [-17.7635, -48.6094],
    [-17.7630, -48.6094], [-17.7625, -48.6094], [-17.7620, -48.6094],
    [-17.7615, -48.6094], [-17.7615, -48.6099], [-17.7615, -48.6104],
    [-17.7615, -48.6109], [-17.7615, -48.6114], [-17.7615, -48.6119],
    [-17.7615, -48.6124], [-17.7620, -48.6124], [-17.7625, -48.6124],
    [-17.7630, -48.6124], [-17.7635, -48.6124], [-17.7640, -48.6124],
    [-17.7645, -48.6124], [-17.7650, -48.6124], [-17.7655, -48.6124],
    [-17.7660, -48.6124], [-17.7665, -48.6124], [-17.7665, -48.6119],
    [-17.7665, -48.6114], [-17.7665, -48.6109], [-17.7665, -48.6104],
    [-17.7665, -48.6099], [-17.7665, -48.6094], [-17.7665, -48.6089],
    [-17.7665, -48.6084], [-17.7670, -48.6084], [-17.7675, -48.6084],
    [-17.7680, -48.6084], [-17.7685, -48.6084], [-17.7690, -48.6084],
    [-17.7695, -48.6084], [-17.7695, -48.6079], [-17.7695, -48.6074],
    [-17.7695, -48.6069], [-17.7695, -48.6064], [-17.7695, -48.6059],
    [-17.7695, -48.6054], [-17.7690, -48.6054], [-17.7685, -48.6054],
    [-17.7680, -48.6054], [-17.7675, -48.6054], [-17.7670, -48.6054],
    [-17.7665, -48.6054], [-17.7660, -48.6054], [-17.7655, -48.6054],
    [-17.7655, -48.6049], [-17.7655, -48.6044], [-17.7655, -48.6039],
    [-17.7655, -48.6034], [-17.7655, -48.6029], [-17.7655, -48.6024],
    [-17.7650, -48.6024], [-17.7645, -48.6024], [-17.7640, -48.6024],
    [-17.7635, -48.6024], [-17.7630, -48.6024], [-17.7625, -48.6024],
    [-17.7620, -48.6024], [-17.7615, -48.6024],
  ];

  const faultsData: FaultCategory[] = [
    {
      title: 'FALTAS ELIMINATÓRIAS (Quatro Pontos)', type: 'eliminatory', points: 4,
      faults: [
        { code: 'I-A', description: 'Desobedecer a sinalização semafórica e de parada obrigatória', count: 0 },
        { code: 'I-B', description: 'Avançar sobre o meio fio', count: 0 },
        { code: 'I-C', description: 'Não colocar o veículo na área balizada, em no máximo três tentativas, no tempo estabelecido', count: 0 },
        { code: 'I-D', description: 'Avançar sobre o balizamento demarcado quando do estacionamento do veículo na vaga', count: 0 },
        { code: 'I-E', description: 'Transitar em contramão de direção', count: 0 },
        { code: 'I-F', description: 'Não completar a realização de todas as etapas do exame', count: 0 },
        { code: 'I-G', description: 'Avançar a via preferencial', count: 0 },
        { code: 'I-H', description: 'Provocar acidente durante a realização do exame', count: 0 },
        { code: 'I-I', description: 'Exceder a velocidade regulamentada para a via', count: 0 },
        { code: 'I-J', description: 'Cometer qualquer outra infração de trânsito de natureza gravíssima', count: 0 },
      ]
    },
    {
      title: 'FALTAS GRAVES (Três Pontos)', type: 'severe', points: 3,
      faults: [
        { code: 'II-A', description: 'Desobedecer a sinalização da via ou do agente da autoridade de trânsito', count: 0 },
        { code: 'II-B', description: 'Não observar as regras de ultrapassagem ou de mudança de direção', count: 0 },
        { code: 'II-C', description: 'Não dar preferência de passagem ao pedestre que estiver atravessando a via transversal para onde se dirige o veículo, ou ainda quando o pedestre não haja concluído a travessia, mesmo que ocorra sinal verde para o mesmo', count: 0 },
        { code: 'II-D', description: 'Manter a porta do veículo aberta ou semi-aberta durante o percurso da prova ou parte dele', count: 0 },
        { code: 'II-E', description: 'Não sinalizar com antecedência a manobra pretendida ou sinalizá-la incorretamente', count: 0 },
        { code: 'II-F', description: 'Não usar devidamente o cinto de segurança', count: 1 },
        { code: 'II-G', description: 'Perder o controle da direção do veículo em movimento', count: 0 },
        { code: 'II-H', description: 'Cometer qualquer outra infração de trânsito de natureza grave', count: 0 },
      ]
    },
    {
      title: 'FALTAS MÉDIAS (Dois Pontos)', type: 'medium', points: 2,
      faults: [
        { code: 'III-A', description: 'Executar o percurso da prova, no todo ou parte dele sem estar o freio de mão inteiramente livre', count: 0 },
        { code: 'III-B', description: 'Trafegar em velocidade inadequada para as condições adversas do local, da circulação, do veículo e do clima', count: 0 },
        { code: 'III-C', description: 'Interromper o funcionamento do motor, sem justa razão, após o início da prova', count: 1 },
        { code: 'III-D', description: 'Fazer conversão incorretamente', count: 0 },
        { code: 'III-E', description: 'Usar a buzina sem necessidade ou em local proibido', count: 0 },
        { code: 'III-F', description: 'Desengrenar o veículo nos declives', count: 0 },
        { code: 'III-G', description: 'Colocar o veículo em movimento, sem observar as cautelas necessárias', count: 1 },
        { code: 'III-H', description: 'Usar o pedal da embreagem, antes de usar o pedal de freio nas frenagens', count: 0 },
        { code: 'III-I', description: 'Entrar nas curvas com a engrenagem de tração do veículo em ponto neutro', count: 0 },
        { code: 'III-J', description: 'Engrenar ou utilizar as marchas de maneira incorreta, durante o percurso', count: 1 },
        { code: 'III-K', description: 'Cometer qualquer outra infração de trânsito de natureza média', count: 0 },
      ]
    },
    {
      title: 'FALTAS LEVES (Um Ponto)', type: 'light', points: 1,
      faults: [
        { code: 'IV-A', description: 'Provocar movimentos irregulares no veículo, sem motivo justificado', count: 0 },
        { code: 'IV-B', description: 'Ajustar incorretamente o banco de veículos destinado ao condutor', count: 0 },
        { code: 'IV-C', description: 'Não ajustar devidamente os espelhos retrovisores', count: 1 },
        { code: 'IV-D', description: 'Apoiar o pé no pedal da embreagem com o veículo engrenado e em movimento', count: 0 },
        { code: 'IV-E', description: 'Utilizar ou interpretar incorretamente os instrumentos do painel do veículo', count: 0 },
        { code: 'IV-F', description: 'Dar partida ao veículo com a engrenagem de tração ligada', count: 0 },
        { code: 'IV-G', description: 'Tentar movimentar o veículo com a engrenagem de tração em ponto neutro', count: 0 },
        { code: 'IV-H', description: 'Cometer qualquer outra infração de trânsito de natureza leve', count: 0 },
      ]
    },
  ];

  const totalPoints = faultsData.reduce((acc, category) => {
    return acc + category.faults.reduce((catAcc, fault) => catAcc + fault.count * category.points, 0);
  }, 0);

  const anyFaultsCommitted = faultsData.some(category => category.faults.some(fault => fault.count > 0));

  const renderAuditStatusButton = () => {
    switch (auditStatus) {
      case 'validated':
        return (
          <Button variant="outline" className="bg-green-600 border-green-600 text-white hover:bg-green-700 no-print mt-2">
            <CheckCircle className="mr-2 h-4 w-4" />
            Aula Validada
          </Button>
        );
      case 'invalidated':
        return (
          <Button variant="outline" className="bg-red-600 border-red-600 text-white hover:bg-red-700 no-print mt-2">
            <XCircle className="mr-2 h-4 w-4" />
            Aula Invalidada
          </Button>
        );
      case 'pending-audit':
      default:
        return (
          <Button variant="outline" className="bg-yellow-400 border-yellow-400 text-yellow-900 hover:bg-yellow-500 no-print mt-2">
            <AlertCircle className="mr-2 h-4 w-4" />
            Aula com Alerta!
          </Button>
        );
    }
  };


  return (
    <div className="min-h-screen bg-gray-100 font-sans text-gray-800">
      <main className="container mx-auto p-4 md:p-8">
        <div className="max-w-5xl mx-auto space-y-6">
          
          <div id="report-header">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4 border-b pb-4">
              <img 
                src="/images/logo.svg" 
                alt="Logo" 
                width={180} 
                height={40} 
                data-ai-hint="logo"
              />
              <div className="text-right">
                  <h1 className="text-2xl font-bold text-gray-800">RELATÓRIO DE AULA PRÁTICA</h1>
                  <p className="text-sm text-gray-500">Conforme Portaria DETRAN-GO nº 994 de 19 de setembro de 2024</p>
                  {renderAuditStatusButton()}
              </div>
            </header>
          </div>

          {/* Main Content */}
          <div className="space-y-6 printable-area">

            <InfoCard id="card-dados-aula" className="report-card" title="Dados da Aula" icon={<CheckCircle className="text-primary h-5 w-5" />}>
              <div className="space-y-4">
                <InfoGrid>
                  <InfoItem label="Número da Aula" value={<span className="text-blue-600 font-bold text-lg">257</span>} />
                  <InfoItem label="Data" value="24/07/2025" />
                  <InfoItem label="Horário Inicial" value="15:43:40" />
                  <InfoItem label="Horário Final" value="16:33:40" />
                  <InfoItem label="Duração da Aula" value={<span className="text-blue-600 font-bold">50 minutos</span>} />
                  <InfoItem label="KM Inicial" value="197.000 km" />
                  <InfoItem label="KM Final" value="197.008 km" />
                  <InfoItem label="Distância Percorrida" value="8,00 km" />
                </InfoGrid>
                <Separator />
                <div className="space-y-2">
                    <span className="text-xs text-gray-500">Progresso do Curso</span>
                    <Progress value={5} className="h-2" />
                    <p className="text-xs text-right text-gray-500">1 aula de 20</p>
                </div>
              </div>
            </InfoCard>

            <InfoCard id="card-info-autoescola" className="report-card" title="Informações da Autoescola" icon={<FileCheck className="text-primary h-5 w-5" />}>
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                <InfoItem label="Nome" value="Autoescola Segura no Trânsito" />
                <InfoItem label="CNPJ" value="12.345.678/0001-90" />
                <InfoItem label="Diretor Geral" value="Carlos Eduardo" />
                <InfoItem label="Credenciamento" value="DETRAN-GO-12345" />
                <InfoItem label="Cidade/Estado" value="Goiânia - GO" />
                <InfoItem label="Contato" value="(62) 3555-1234 / contato@autoescolasegura.com.br" />
              </div>
            </InfoCard>
            
            <InfoCard id="card-info-aluno" className="report-card" title="Informações do Aluno" icon={<User className="text-primary h-5 w-5" />}>
              <InfoGrid>
                <InfoItem label="Nome Completo" value="Pedro Wilson Arantes Moura" />
                <InfoItem label="CPF" value="021.305.055" />
                <InfoItem label="Categoria" value="B" />
                <InfoItem label="Processo" value="Primeira Habilitação" />
                <InfoItem label="Renach" value="GO123456789" />
              </InfoGrid>
            </InfoCard>

            <InfoCard id="card-info-instrutor" className="report-card" title="Informações do Instrutor" icon={<User className="text-primary h-5 w-5" />}>
              <InfoGrid>
                <InfoItem label="Nome Completo" value="João Silva" />
                <InfoItem label="CPF" value="123.456.789-00" />
                <InfoItem label="Credencial" value="DETRAN-GO 54321" />
                <InfoItem label="Validade da Credencial" value="31/12/2025" />
              </InfoGrid>
            </InfoCard>
            
            <InfoCard id="card-info-veiculo" className="report-card" title="Informações do Veículo" icon={<FileCheck className="text-primary h-5 w-5" />}>
              <InfoGrid>
                <InfoItem label="Modelo" value="Volkswagen Gol" />
                <InfoItem label="Placa" value="JIK-1234" />
                <InfoItem label="Ano" value="2022" />
                <InfoItem label="Nº do Chassi" value="9BWHE21B24E89243" />
                <InfoItem label="Credenciamento" value="DETRAN-GO 98765" />
              </InfoGrid>
            </InfoCard>

            <InfoCard id="card-biometria" className="report-card" title="Registros Biométricos" icon={<Fingerprint className="text-primary h-5 w-5" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Instructor Biometrics */}
                <div className="p-4 border rounded-lg bg-gray-50/50 space-y-3">
                  <p className="font-semibold text-center text-sm">Instrutor: João Silva</p>
                  <div className="flex items-center gap-4">
                    <Image 
                      src="https://placehold.co/80x80.png" 
                      alt="Biometria Início" 
                      width={80} height={80} 
                      className="rounded-md border cursor-pointer" 
                      data-ai-hint="man face"
                      onClick={() => setSelectedBiometric({
                        title: 'Verificação Biométrica - Início da Aula (Instrutor)',
                        dbPhoto: 'https://placehold.co/400x400.png', dbPhotoHint: 'man face',
                        appPhoto: 'https://placehold.co/400x400.png', appPhotoHint: 'man face slight smile'
                      })}
                    />
                    <div>
                      <p className="font-medium text-xs">Início da Aula</p>
                      <p className="text-xs text-gray-500">24/07/2025 15:43:35</p>
                      <p className="text-xs text-green-600 font-medium">Verificado</p>
                    </div>
                  </div>
                   <div className="flex items-center gap-4">
                    <Image 
                      src="https://placehold.co/80x80.png" 
                      alt="Biometria Fim" 
                      width={80} 
                      height={80} 
                      className="rounded-md border cursor-pointer" 
                      data-ai-hint="man face"
                      onClick={() => setSelectedBiometric({
                        title: 'Verificação Biométrica - Fim da Aula (Instrutor)',
                        dbPhoto: 'https://placehold.co/400x400.png', dbPhotoHint: 'man face',
                        appPhoto: 'https://placehold.co/400x400.png', appPhotoHint: 'man face tired'
                      })}
                    />
                    <div>
                      <p className="font-medium text-xs">Fim da Aula</p>
                      <p className="text-xs text-gray-500">24/07/2025 16:33:45</p>
                      <p className="text-xs text-green-600 font-medium">Verificado</p>
                    </div>
                  </div>
                </div>
                {/* Student Biometrics */}
                <div className="p-4 border rounded-lg bg-gray-50/50 space-y-3">
                   <p className="font-semibold text-center text-sm">Aluno: Pedro Wilson Arantes Moura</p>
                  <div className="flex items-center gap-4">
                    <Image 
                      src="https://placehold.co/80x80.png" 
                      alt="Biometria Início" 
                      width={80} 
                      height={80} 
                      className="rounded-md border cursor-pointer" 
                      data-ai-hint="man face"
                      onClick={() => setSelectedBiometric({
                        title: 'Verificação Biométrica - Início da Aula (Aluno)',
                        dbPhoto: 'https://placehold.co/400x400.png', dbPhotoHint: 'young man face',
                        appPhoto: 'https://placehold.co/400x400.png', appPhotoHint: 'young man face slight smile'
                      })}
                    />
                    <div>
                      <p className="font-medium text-xs">Início da Aula</p>
                      <p className="text-xs text-gray-500">24/07/2025 15:43:38</p>
                      <p className="text-xs text-green-600 font-medium">Verificado</p>
                    </div>
                  </div>
                   <div className="flex items-center gap-4">
                    <Image 
                      src="https://placehold.co/80x80.png" 
                      alt="Biometria Fim" 
                      width={80} 
                      height={80} 
                      className="rounded-md border cursor-pointer" 
                      data-ai-hint="man face"
                      onClick={() => setSelectedBiometric({
                        title: 'Verificação Biométrica - Fim da Aula (Aluno)',
                        dbPhoto: 'https://placehold.co/400x400.png', dbPhotoHint: 'young man face',
                        appPhoto: 'https://placehold.co/400x400.png', appPhotoHint: 'young man face tired'
                      })}
                    />
                    <div>
                      <p className="font-medium text-xs">Fim da Aula</p>
                      <p className="text-xs text-gray-500">24/07/2025 16:33:42</p>
                      <p className="text-xs text-green-600 font-medium">Verificado</p>
                    </div>
                  </div>
                </div>
              </div>
            </InfoCard>

            <InfoCard id="card-conteudo" className="report-card" title="Conteúdo Programático da Aula" icon={<CheckCircle className="text-primary h-5 w-5" />}>
              <div className="space-y-6">
                <SyllabusSection title="I - Conceitos Básicos">
                  <SyllabusItem text="Verificação das condições dos equipamentos obrigatórios" checked={true} />
                  <SyllabusItem text="Acomodação e regulagem do equipamento do aluno" checked={true} />
                  <SyllabusItem text="Localização e conhecimento dos comandos de um veículo" checked={true} />
                  <SyllabusItem text="Ligando o motor" checked={true} />
                </SyllabusSection>

                <SyllabusSection title="Aprendendo a Conduzir">
                   <SyllabusItem text="Uso dos pedais e início da condução em 1ª marcha" checked={true} />
                   <SyllabusItem text="Mudança da 1ª para a 2ª marcha" checked={true} />
                   <SyllabusItem text="Mudança da 2ª para a 3ª marcha" checked={true} />
                   <SyllabusItem text="Mudança da 4ª para a 5ª marcha" checked={false} />
                   <SyllabusItem text="Controlando a condução veicular" checked={true} />
                   <SyllabusItem text="Efetuando uma curva" checked={true} />
                   <SyllabusItem text="Aperfeiçoando o uso da alavanca de câmbio" checked={false} />
                   <SyllabusItem text="Aperfeiçoando o uso da embreagem" checked={true} />
                   <SyllabusItem text="Aperfeiçoando o uso do freio" checked={true} />
                   <SyllabusItem text="Domínio do veículo em marcha à ré" checked={false} />
                </SyllabusSection>

                <SyllabusSection title="Aprendizado da Circulação">
                  <SyllabusItem text="Posição do veículo na via, velocidade e observação" checked={true} />
                  <SyllabusItem text="Entrada no fluxo do tráfego de veículos na via" checked={true} />
                  <SyllabusItem text="Movimento lateral e transposição de faixa" checked={false} />
                  <SyllabusItem text="Parada e estacionamento" checked={true} />
                  <SyllabusItem text="Ultrapassagens" checked={false} />
                  <SyllabusItem text="Passagem em interseções (cruzamentos)" checked={true} />
                  <SyllabusItem text="Mudança de sentido" checked={false} />
                  <SyllabusItem text="Condução e circulação por vias urbanas" checked={true} />
                  <SyllabusItem text="Condução em vias de tráfego intenso" checked={false} />
                  <SyllabusItem text="Condução em condições atmosféricas adversas" checked={false} />
                  <SyllabusItem text="Condução e circulação noturna" checked={false} />
                </SyllabusSection>

                <SyllabusSection title="II - Condução Segura">
                  <SyllabusItem text="A partida e a mudança de marchas" checked={true} />
                  <SyllabusItem text="Utilizando os freios" checked={true} />
                  <SyllabusItem text="Circulação e velocidade" checked={true} />
                  <SyllabusItem text="Aclives e declives" checked={false} />
                  <SyllabusItem text="Curvas" checked={true} />
                  <SyllabusItem text="Condução em congestionamentos" checked={false} />
                  <SyllabusItem text="Entrada e saída no fluxo de tráfego" checked={true} />
                  <SyllabusItem text="Obstáculos durante a condução" checked={false} />
                </SyllabusSection>
              </div>
            </InfoCard>

            <InfoCard id="card-faltas" className="report-card" title="Faltas Cometidas Durante a Aula" icon={<AlertCircle className="text-primary h-5 w-5" />}>
              <div className="space-y-6">
                {!anyFaultsCommitted ? (
                    <div className="text-center text-gray-500 py-4">
                        <CheckCircle className="mx-auto h-8 w-8 text-green-500 mb-2" />
                        <p>Nenhuma falta cometida durante a aula.</p>
                    </div>
                ) : (
                    faultsData.map((category, index) => {
                        const committedFaults = category.faults.filter(fault => fault.count > 0);
                        if (committedFaults.length === 0) return null;

                        return (
                            <div key={index}>
                                <h3 className="font-bold text-sm mb-2 text-gray-800">{category.title}</h3>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-[10%]">Índice</TableHead>
                                            <TableHead>Descrição da Falta</TableHead>
                                            <TableHead className="w-[10%] text-center">Faltas</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {committedFaults.map((fault) => (
                                            <TableRow key={fault.code}>
                                                <TableCell className="font-medium">{fault.code}</TableCell>
                                                <TableCell>{fault.description}</TableCell>
                                                <TableCell className="text-center">{fault.count}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        );
                    })
                )}

                <div className="flex justify-end pt-4">
                  <div className="w-full max-w-sm">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tipo de Falta</TableHead>
                          <TableHead className="text-center">Qtde de Faltas</TableHead>
                          <TableHead className="text-center">Pontos</TableHead>
                          <TableHead className="text-center">Resultado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {faultsData.map((category) => {
                          const quantity = category.faults.reduce((acc, fault) => acc + fault.count, 0);
                          const result = quantity * category.points;
                          return (
                            <TableRow key={category.type}>
                              <TableCell className="font-medium">{category.title.split('(')[0].trim()}</TableCell>
                              <TableCell className="text-center">{quantity}</TableCell>
                              <TableCell className="text-center">{category.points}</TableCell>
                              <TableCell className="text-center">{result}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                      <TableFooter>
                         <TableRow>
                          <TableCell colSpan={3} className="text-right font-bold">Total de Pontos</TableCell>
                          <TableCell className="text-center font-bold">{totalPoints}</TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </div>
                </div>
              </div>
            </InfoCard>

            <InfoCard id="card-avisos" className="report-card" title="Avisos da Aula" icon={<AlertCircle className="text-primary h-5 w-5" />}>
              <div className="space-y-4">
                {auditedAlerts.map((aviso, index) => (
                  <div key={index} className="flex items-start justify-between gap-4 p-4 rounded-lg border bg-white shadow-sm">
                    <div className="flex-1 space-y-2">
                       <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-4 w-4" />
                          <span>{aviso.time}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-4 w-4" />
                          <span>Lat: {aviso.lat}, Lng: {aviso.lng}</span>
                        </div>
                        <div className="flex items-center gap-1.5 font-medium text-gray-800">
                           <Tag className="h-4 w-4" />
                           <span>{aviso.description}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-start gap-2">
                         <div
                          className={cn('flex items-center gap-1.5 text-sm font-medium', {
                            'text-green-600': aviso.alert.type === 'success',
                            'text-yellow-600': aviso.alert.type === 'warning',
                             'text-red-600': aviso.alert.type === 'destructive',
                          })}
                        >
                          <aviso.alert.icon className="h-4 w-4" />
                          <span>{aviso.alert.text}</span>
                        </div>
                        <Button variant="outline" size="sm" className="h-8" onClick={() => setSelectedLocation([aviso.lat, aviso.lng])}>
                          <Map className="mr-2 h-4 w-4" />
                          Ver no Mapa
                        </Button>
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-2">
                      {aviso.eventImage && (
                        <Image
                          src={aviso.eventImage}
                          alt="Event photo"
                          width={120}
                          height={80}
                          className="rounded-md border object-cover"
                          data-ai-hint={aviso.eventHint ?? ''}
                        />
                      )}
                      <div
                        className="w-[120px] h-[80px] rounded-md border overflow-hidden cursor-pointer"
                        onClick={() => setSelectedLocation([aviso.lat, aviso.lng])}
                      >
                        <MiniMapDisplay center={[aviso.lat, aviso.lng]} zoom={15} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </InfoCard>
            
            <InfoCard id="card-resumo" className="report-card" title="Resumo da Aula" icon={<CheckCircle className="text-primary h-5 w-5" />}>
                <div className="space-y-6">
                    <p className="text-sm leading-relaxed">
                        O aluno <strong>Pedro Wilson Arantes Moura</strong> realizou sua aula prática de direção veicular no dia <strong>24/07/2025</strong>, com duração de <strong>50 minutos</strong>, percorrendo um total de <strong>2.28 km</strong>. A aula teve início às <strong>15:43:40</strong> e término às <strong>16:33:40</strong>, com registro de 4 pontos de verificação ao longo do trajeto.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <h4 className="font-semibold mb-3">Avaliação de Desempenho</h4>
                            <div className="space-y-2">
                                <StarRating label="Controle do Veículo" rating={80} />
                                <StarRating label="Observância das Leis de Trânsito" rating={90} />
                                <StarRating label="Atenção ao Trânsito" rating={80} />
                                <StarRating label="Manobras e Estacionamento" rating={60} />
                                <StarRating label="Avaliação Geral" rating={80} />
                            </div>
                        </div>
                         <div>
                            <h4 className="font-semibold mb-3">Comentários do Instrutor</h4>
                            <div className="text-sm p-4 bg-gray-50 rounded-md border space-y-3">
                                <div>
                                    <h5 className="font-semibold">Pontos positivos:</h5>
                                    <ul className="list-disc list-inside text-gray-600">
                                        <li>O aluno demonstrou bom controle do veículo durante a aula, mantendo atenção constante ao trânsito e sinalizações.</li>
                                        <li>Houve boa observância das leis de trânsito, respeitando limites de velocidade e sinalizações.</li>
                                    </ul>
                                </div>
                                <div>
                                    <h5 className="font-semibold">Pontos a melhorar:</h5>
                                    <ul className="list-disc list-inside text-gray-600">
                                        <li>Manobras de estacionamento precisam ser aprimoradas.</li>
                                        <li>Maior atenção aos retrovisores durante mudanças de faixa.</li>
                                    </ul>
                                </div>
                                 <div>
                                    <h5 className="font-semibold">Recomendação:</h5>
                                    <p className="text-gray-600">Recomendo praticar mais manobras de estacionamento na próxima aula.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </InfoCard>

            <InfoCard id="card-trajeto" className="report-card" title="Trajeto e Coordenadas" icon={<Map className="text-primary h-5 w-5" />}>
                <div className="mb-2">
                  <p className="text-xs text-gray-500">Quilometragem Total</p>
                  <p className="font-medium text-gray-800">4.80 km</p>
                </div>
                <div className="aspect-video bg-gray-200 rounded-md overflow-hidden">
                    <MapDisplay center={[-17.765, -48.6074]} zoom={15} route={routeCoordinates} alerts={auditedAlerts} />
                </div>
            </InfoCard>
            
            <InfoCard id="card-assinaturas" className="report-card" title="Assinaturas Digitais" icon={<FileCheck className="text-primary h-5 w-5" />}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-4 border rounded-lg bg-gray-50/50 flex flex-col items-center justify-center text-center">
                        <div className="w-full h-24 mb-2 flex items-center justify-center">
                            <Image src="https://placehold.co/200x80.png" alt="Assinatura Instrutor" width={200} height={80} className="object-contain" data-ai-hint="handwritten signature" />
                        </div>
                        <Separator className="my-2" />
                        <p className="font-semibold text-sm">Instrutor: João Silva</p>
                        <p className="text-xs text-gray-500">Assinado em 24/07/2025 16:35:48</p>
                    </div>
                    <div className="p-4 border rounded-lg bg-gray-50/50 flex flex-col items-center justify-center text-center">
                        <div className="w-full h-24 mb-2 flex items-center justify-center">
                             <Image src="https://placehold.co/200x80.png" alt="Assinatura Aluno" width={200} height={80} className="object-contain" data-ai-hint="handwritten signature" />
                        </div>
                        <Separator className="my-2" />
                        <p className="font-semibold text-sm">Aluno: Pedro Wilson Arantes Moura</p>
                        <p className="text-xs text-gray-500">Assinado em 24/07/2025 16:36:12</p>
                    </div>
                </div>
            </InfoCard>
            
            <Card className="shadow-md report-card" id="card-verificacao">
                <CardContent className="p-6 text-center space-y-2">
                    <div className="flex justify-center items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600"/>
                        <p className="font-semibold">Documento Verificado e Autenticado</p>
                    </div>
                    <p className="text-xs text-gray-600">Este documento é um relatório oficial de aula prática de direção veicular, credenciado e exigido pela Portaria DETRAN-GO nº 994 de 19 de setembro de 2024.</p>
                    <p className="font-mono text-sm font-bold text-gray-800 bg-gray-100 py-1 px-3 rounded-md inline-block">DETRAN-GO-257-2025072328-781031B</p>
                    <p className="text-xs text-gray-500">A autenticidade deste documento pode ser verificada no site do DETRAN-GO.</p>
                </CardContent>
            </Card>

            <Card className="shadow-md report-card" id="card-emissao">
                <CardContent className="p-4 text-center text-sm">
                    <p>
                        Relatório emitido em {generatedDate}, pelo aluno <strong>Pedro Wilson Arantes Moura</strong> (CPF: <strong>021.305.055</strong>).
                    </p>
                </CardContent>
            </Card>

          </div>

          {/* Footer */}
          <footer className="flex flex-col items-center justify-center gap-4 pt-6 mt-6 border-t no-print text-center">
            <div className="space-y-1">
                <p className="text-xs text-gray-600">Este relatório foi gerado e fiscalizado por uma entidade credenciada pelo DETRAN-GO para o monitoramento de aulas práticas, conforme a Portaria nº 994 de 19 de setembro de 2024.</p>
                <p className="text-xs text-gray-500">A autenticidade e veracidade dos dados são de responsabilidade da entidade de monitoramento e podem ser verificadas junto ao órgão de trânsito.</p>
            </div>
             <div className="flex gap-2">
                <Link href="/reports">
                  <Button variant="outline">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Recuar Relatórios
                  </Button>
                </Link>
                <Button variant="outline" onClick={() => setIsAuditModalOpen(true)}>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Auditar Aula
                </Button>
                <Button onClick={handlePrint}>
                    <Printer className="mr-2 h-4 w-4" />
                    Imprimir
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button>
                      Exportar Relatório
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onSelect={() => handleExportPDF('color')}>
                      <FileText className="mr-2 h-4 w-4" />
                      Exportar para PDF (Colorido)
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => handleExportPDF('bw')}>
                      <FileText className="mr-2 h-4 w-4" />
                      Exportar para PDF (P&B)
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={handleExportXLS}>
                      <FileText className="mr-2 h-4 w-4" />
                      Exportar para XLS
                    </DropdownMenuItem>
                     <DropdownMenuItem onSelect={handleExportTXT}>
                      <FileText className="mr-2 h-4 w-4" />
                      Exportar para TXT
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
            </div>
          </footer>

        </div>
      </main>

      {/* Audit Modal */}
      <Dialog open={isAuditModalOpen} onOpenChange={setIsAuditModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-6 w-6" />
              Auditoria de Alertas da Aula
            </DialogTitle>
            <DialogDescription>
              Marque a caixa para validar um alerta. Desmarque para invalidar e adicione uma justificativa.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto p-1 pr-4 space-y-4">
            {auditedAlerts.map((aviso, index) => (
              <div key={index} className="flex items-start gap-4 p-4 border rounded-md">
                <Checkbox
                  id={`audit-${index}`}
                  checked={!!aviso.validated}
                  onCheckedChange={(checked) => handleAuditCheck(index, !!checked)}
                  className="mt-1"
                />
                <div className="grid gap-2 w-full">
                  <label htmlFor={`audit-${index}`} className="font-semibold cursor-pointer">{aviso.description}</label>
                  <div
                      className={cn('flex items-center gap-1.5 text-sm', {
                        'text-green-600': aviso.alert.type === 'success',
                        'text-yellow-600': aviso.alert.type === 'warning',
                        'text-red-600': aviso.alert.type === 'destructive',
                      })}
                    >
                      <aviso.alert.icon className="h-4 w-4" />
                      <span className="font-medium">{aviso.alert.text}</span>
                  </div>
                   <div className="text-xs text-gray-500">
                      <span>{aviso.time}</span> | <span>Lat: {aviso.lat}, Lng: {aviso.lng}</span>
                   </div>
                   <Textarea
                     placeholder="Escreva a justificativa para invalidar este alerta..."
                     value={aviso.justification}
                     onChange={(e) => handleJustificationChange(index, e.target.value)}
                     disabled={!!aviso.validated}
                     className="mt-2 text-sm"
                   />
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAuditModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleOpenConfirmModal}>Salvar e Continuar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Confirm Audit Modal */}
      <Dialog open={isConfirmAuditModalOpen} onOpenChange={setIsConfirmAuditModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Auditoria</DialogTitle>
            <DialogDescription>
              Para finalizar a auditoria, por favor, insira sua matrícula de auditor e escolha uma das ações abaixo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="auditorId">Matrícula do Auditor</Label>
              <Input
                id="auditorId"
                placeholder="Digite sua matrícula"
                value={auditorId}
                onChange={(e) => setAuditorId(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="destructive"
              onClick={() => handleConfirmAudit('invalidate')}
              disabled={!auditorId}
            >
              <X className="mr-2 h-4 w-4" />
              Invalidar Aula
            </Button>
            <Button
              variant="default"
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleConfirmAudit('validate')}
              disabled={!auditorId}
            >
              <Check className="mr-2 h-4 w-4" />
              Validar Aula
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Map Lightbox */}
      <Dialog open={!!selectedLocation} onOpenChange={() => setSelectedLocation(null)}>
        <DialogContent className="max-w-3xl p-2">
          <DialogHeader className="sr-only">
            <DialogTitle>Visualização do Mapa do Evento</DialogTitle>
            <DialogDescription>Um mapa mostrando a localização do evento selecionado.</DialogDescription>
          </DialogHeader>
          <div className="aspect-video relative overflow-hidden rounded-md">
            {selectedLocation && (
              <MiniMapDisplay center={selectedLocation} zoom={16} />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Biometric Lightbox */}
      <Dialog open={!!selectedBiometric} onOpenChange={() => setSelectedBiometric(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{selectedBiometric?.title}</DialogTitle>
            <DialogDescription>
              Comparação da foto de cadastro com a foto capturada durante a aula para verificação biométrica.
            </DialogDescription>
          </DialogHeader>
          {selectedBiometric && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
              <div className="space-y-2">
                <h3 className="font-semibold text-center">Foto Cadastrada (BD)</h3>
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border">
                  <Image 
                    src={selectedBiometric.dbPhoto} 
                    alt="Foto do banco de dados" 
                    width={400} 
                    height={400} 
                    className="object-cover w-full h-full"
                    data-ai-hint={selectedBiometric.dbPhotoHint} 
                  />
                </div>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-center">Foto da Aula (App)</h3>
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border">
                   <Image 
                    src={selectedBiometric.appPhoto} 
                    alt="Foto do aplicativo" 
                    width={400} 
                    height={400} 
                    className="object-cover w-full h-full"
                    data-ai-hint={selectedBiometric.appPhotoHint}
                   />
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

